import { NgModule, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { NotTenantGuard, TenancyService, TenantGuard } from '@core/services/common';
import { CheckRedirectGuard } from '@core/services/common/guards/check-redirect.guard';
import { EmptyComponentComponent } from './empty-component/empty-component.component';
import { RedirectToTenantGuard } from '@core/services/common/guards/redirect-to-tenant.guard';

const routes: Routes = [
  {
    path: '',
    component: EmptyComponentComponent,
    canActivate: [CheckRedirectGuard],
  },
  {
    path: 'admin',
    canActivate: [NotTenantGuard],
    loadChildren: () => import('./views/pages/central/central.module').then(m => m.CentralModule),
  },
  {
    // Contratação pública: o visitante chega do site com `?plan=<id>`, sem
    // conta e sem tenant. Fica sob `NotTenantGuard` pela mesma razão de
    // `/admin` — num subdomínio de empresa a rota não existe, e o guard
    // manda para o painel dela em vez de servir uma tela de compra a quem
    // já é cliente.
    path: 'contratar',
    canActivate: [NotTenantGuard],
    loadChildren: () => import('./views/pages/contratar/contratar.module').then(m => m.ContratarModule),
  },
  {
    // O validador público de contratos: quem recebeu um contrato por e-mail
    // confere, com o código do convite e o CPF/CNPJ de uma das partes, se ele
    // foi assinado, por quem e quando. Sem conta e sem sessão. Fica sob
    // `NotTenantGuard` pela mesma razão de `/contratar`: a rota da api
    // (`contract-validator/check`) só existe nos domínios CENTRAIS, e o guard
    // garante que o `apiUrl` desta página é o do apex, nunca o de um tenant.
    path: 'validar',
    canActivate: [NotTenantGuard],
    loadChildren: () => import('./views/pages/validador/validador.module').then(m => m.ValidadorModule),
  },
  {
    path: 'painel',
    canActivate: [TenantGuard],
    loadChildren: () => import('./views/pages/tenant/tenant.module').then(m => m.TenantModule),
  },
  {
    // A área do contador. **Sem `NotTenantGuard`**: aquela guarda manda para o
    // painel do tenant quem tem subdomínio, e o contador chega por um host sem
    // subdomínio — mas o caminho é também o SELETOR de contexto em QA e em
    // desenvolvimento, onde `accountantUrl`, `centralUrl` e `appUrl` são o
    // mesmo host (ver `TenancyService.isAccountantContext`, ruling R-09).
    path: 'contador',
    loadChildren: () => import('./views/pages/accountant/accountant.module').then(m => m.AccountantModule),
  },
  {
    path: 'invalido',
    loadChildren: () => import('./views/pages/error/error.module').then(m => m.ErrorModule),
  },
  {
    path: 'inativo',
    loadChildren: () => import('./views/pages/error/error.module').then(m => m.ErrorModule),
  },
  {
    path: 'autenticar',
    loadChildren: () => import('./views/pages/common/two-factor/two-factor.module').then(m => m.TwoFactorModule),
  },
  {
    // A PÁGINA PÚBLICA DE PAGAMENTO. Rota de TOPO, sem `canActivate` nenhum —
    // quem abre é o pagador, que não tem usuário nem sessão.
    //
    // NÃO pode ficar sob `painel`: aquele ramo carrega `TenantGuard`, que faz
    // uma consulta HTTP de `checkSubdomain` e redireciona por
    // `window.location.href` — um link de cobrança não pode depender disso.
    //
    // Tem de vir ANTES do curinga `**` abaixo, que redireciona por
    // `RedirectToTenantGuard`. O roteador casa na ORDEM: declarada depois,
    // esta rota nunca seria alcançada.
    path: 'pagar',
    loadChildren: () => import('./views/pages/checkout/checkout.module').then(m => m.CheckoutModule),
  },
  {
    path: 'assinar',
    loadComponent: () => import('./views/pages/tenant/contracts/contract-signing.component').then(m => m.ContractSigningComponent),
  },
  {
    path: '**',
    canActivate: [RedirectToTenantGuard],
    pathMatch: 'full',
    component: EmptyComponentComponent,
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
  private tenancyService = inject(TenancyService);

  constructor() {
    const { hostname, pathname } = window.location;

    // SÃO TRÊS CONTEXTOS, não dois. O do contador precisa ser decidido ANTES
    // de `makeSubdomain`: no domínio dele o subdomínio é `undefined` (host
    // reservado), o que é indistinguível do backoffice central — e a diferença
    // decide o realm da API (`accountant-api/v1`) e a chave de sessão.
    if (this.tenancyService.isAccountantContext(hostname, pathname)) {
      this.tenancyService.makeAccountantUrl();
      return;
    }

    const subdomain = this.tenancyService.makeSubdomain(hostname);
    this.tenancyService.makeUrl(subdomain);
  }
}
